import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import {
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useMemo, useState } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { useAppContext } from '../../../providers/AppProvider';
import { useProductionMaterialSummary } from '../../../features/jobs/hooks/useProductionDashboardStats';
import MaterialDrilldownModal from '../../production/MaterialDrilldownModal';

echarts.use([
  TooltipComponent,
  LegendComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
]);

/**
 * The Planned bar's colour.
 *
 * It used to be `info-100`, and Phoenix's -100 tints invert with the theme:
 * #c7ebff on light — a clear pale blue — and #1a2532 on dark, which is very
 * nearly the chart's own background. So on a dark screen the Planned bar was a
 * faint grey smudge behind the Executed one, and the comparison the chart exists
 * to make could not be made.
 *
 * `info-300` is #60c6ff in BOTH themes — the tints stop inverting at 200 — so it
 * reads the same wherever it is looked at, and stays clearly apart from the
 * primary blue of Executed.
 */
const PLANNED_COLOR = getThemeColor => getThemeColor('info-300');

const getDefaultOptions = (getThemeColor, chartData) => {
  const labels = chartData.map(item => item.material);
  const plannedData = chartData.map(item => item.planned_kg);
  const executedData = chartData.map(item => item.executed_kg);

  return {
    // Order matters — it pairs with the series below, and it was reversed and
    // stale (grey for a bar that is not grey). The series set their own colours,
    // so this only ever fed the legend, which is why nobody noticed.
    color: [PLANNED_COLOR(getThemeColor), getThemeColor('primary')],
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'shadow'
      },
      formatter: params => {
        const rows = Array.isArray(params) ? params : [params];
        if (!rows.length) return '';

        const title = rows[0]?.axisValue ?? '';
        const body = rows
          .map(
            item =>
              `${item.marker} ${item.seriesName}: <strong>${Number(
                item.value || 0
              ).toLocaleString('ro-RO', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 3
              })} kg</strong>`
          )
          .join('<br/>');

        return `<strong>${title}</strong><br/>${body}`;
      }
    },
    legend: {
      data: ['Planned', 'Executed'],
      right: 'right',
      width: '100%',
      itemWidth: 16,
      itemHeight: 8,
      itemGap: 20,
      top: 3,
      inactiveColor: getThemeColor('gray-500'),
      textStyle: {
        color: getThemeColor('gray-900'),
        fontWeight: 600,
        fontFamily: 'Nunito Sans'
      }
    },
    xAxis: {
      type: 'category',
      axisLabel: {
        color: getThemeColor('gray-800'),
        interval: 0,
        rotate: labels.length > 4 ? 20 : 0,
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        fontSize: 12.8
      },
      data: labels,
      axisLine: {
        lineStyle: {
          color: getThemeColor('gray-300')
        }
      },
      axisTick: false
    },
    yAxis: {
      type: 'value',
      name: 'kg',
      nameTextStyle: {
        color: getThemeColor('gray-700'),
        fontFamily: 'Nunito Sans',
        fontWeight: 600
      },
      axisPointer: { type: 'none' },
      axisTick: 'none',
      splitLine: {
        lineStyle: {
          color: getThemeColor('gray-200')
        }
      },
      axisLine: { show: false },
      axisLabel: {
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        fontSize: 12.8,
        color: getThemeColor('gray-800'),
        margin: 20,
        verticalAlign: 'bottom',
        formatter: value => Number(value).toLocaleString('ro-RO')
      }
    },
    series: [
      {
        name: 'Planned',
        type: 'bar',
        barWidth: '10px',
        data: plannedData,
        barGap: '30%',
        label: { show: false },
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: PLANNED_COLOR(getThemeColor)
        }
      },
      {
        name: 'Executed',
        type: 'bar',
        data: executedData,
        barWidth: '10px',
        barGap: '30%',
        label: { show: false },
        z: 10,
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: getThemeColor('primary')
        }
      }
    ],
    grid: {
      right: 10,
      left: 10,
      bottom: 10,
      top: '18%',
      containLabel: true
    },
    animation: true
  };
};

const ProductionMaterialBarChart = ({
  height = '316px',
  width = '100%',
  filterCategory
}) => {
  const { getThemeColor } = useAppContext();
  const { data, isLoading, isError, error } = useProductionMaterialSummary();
  // Which material bars the drill-down is open on. Empty = closed.
  const [drilldown, setDrilldown] = useState([]);

  const allData = data?.summary || [];
  const cats = Array.isArray(filterCategory) ? filterCategory : [filterCategory];
  const chartData = allData.filter(item => cats.includes(item.category));

  // Clicking a bar asks "which products is this made of?" — the one question the
  // chart cannot answer, and the reason somebody looks at it. ECharts gives the
  // category INDEX, so the key comes from the same array the bars were built
  // from; reading the axis label back would break on two materials that share a
  // label (PVC NATUR and POLIETILEN NATUR).
  const onEvents = useMemo(
    () => ({
      click: params => {
        const row = chartData[params?.dataIndex];
        if (row?.materialKey) setDrilldown([row.materialKey]);
      }
    }),
    [chartData]
  );


  if (isLoading) {
    return (
      <div
        style={{ height, width }}
        className="d-flex align-items-center justify-content-center"
      >
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading chart...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="mb-0">
        Failed to load material summary: {error?.message || 'Unknown error'}
      </Alert>
    );
  }

  if (!chartData.length) {
    return (
      <div
        style={{ height, width }}
        className="d-flex align-items-center justify-content-center text-700"
      >
        No material data found
      </div>
    );
  }

  return (
    <>
      <ReactEChartsCore
        echarts={echarts}
        option={getDefaultOptions(getThemeColor, chartData)}
        style={{ height, width, cursor: 'pointer' }}
        notMerge={true}
        lazyUpdate={true}
        onEvents={onEvents}
      />

      <MaterialDrilldownModal
        materialKeys={drilldown}
        allMaterials={chartData}
        onToggleKey={key =>
          setDrilldown(prev =>
            prev.includes(key)
              ? // Never down to nothing: removing the last chip would close the
                // modal, which is not what a toggle looks like it does.
                prev.length > 1
                ? prev.filter(k => k !== key)
                : prev
              : [...prev, key]
          )
        }
        onHide={() => setDrilldown([])}
      />
    </>
  );
};

export default ProductionMaterialBarChart;
